// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import Layout from '../Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/apiClient';
import { handleApiError, showSuccess } from '../utils/errorHandler';
import {
    FiUser,
    FiMail,
    FiPhone,
    FiGlobe,
    FiBook,
    FiGithub,
    FiLinkedin,
    FiLock,
    FiShield,
    FiEye,
  FiEyeOff,
  FiLogOut,
    FiAlertTriangle,
    FiCheckCircle,
  FiXCircle,
  FiCreditCard
} from 'react-icons/fi';

// Styled Components
const ProfileContainer = styled.div`
  min-height: calc(100vh - 70px);
`;

const ProfileHeader = styled.section`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  position: relative;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.background};
  margin-right: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.glow};
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`;

const ProfileImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  object-fit: cover;
  margin-right: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.glow};
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  }
`;

const ProfileEmail = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const StatsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const StatItem = styled.div`
  text-align: center;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-width: 80px;
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const TabsContainer = styled.section`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const TabHeader = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 3px;
  }
  
  @media (max-width: 576px) {
    flex-wrap: nowrap;
    justify-content: flex-start;
  }
`;

const TabButton = styled.button`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  background-color: transparent;
  color: ${({ isActive, theme }) => isActive ? theme.colors.primary : theme.colors.textSecondary};
  border-bottom: 2px solid ${({ isActive, theme }) => isActive ? theme.colors.primary : 'transparent'};
  font-weight: 600;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    flex: 1;
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  }
`;

const TabContent = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-weight: 500;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const FormInput = styled.input`
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ hasError, theme }) => hasError ? theme.colors.error : theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  width: 100%;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ hasError, theme }) => hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ hasError, theme }) => hasError ? theme.colors.error + '22' : theme.colors.glow};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  ${({ hasError, theme }) => hasError && `
    background-color: ${theme.colors.error}11;
  `}
`;

const FormSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ hasError, theme }) => hasError ? theme.colors.error : theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  width: 100%;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ hasError, theme }) => hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ hasError, theme }) => hasError ? theme.colors.error + '22' : theme.colors.glow};
  }

  ${({ hasError, theme }) => hasError && `
    background-color: ${theme.colors.error}11;
  `}
`;

const FormTextarea = styled.textarea`
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ hasError, theme }) => hasError ? theme.colors.error : theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  min-height: 120px;
  resize: vertical;
  width: 100%;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ hasError, theme }) => hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ hasError, theme }) => hasError ? theme.colors.error + '22' : theme.colors.glow};
  }

  ${({ hasError, theme }) => hasError && `
    background-color: ${theme.colors.error}11;
  `}
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled(FormInput)`
  padding-right: ${({ theme }) => theme.spacing[12]};
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ValidationError = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  margin-top: ${({ theme }) => theme.spacing[1]};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
    font-size: ${({ theme }) => theme.fonts.sizes.xs};
  }
`;

const SecurityFormGroup = styled(FormGroup)`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: 768px) {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  @media (max-width: 576px) {
    width: 100%;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
    transform: translateY(-2px);
  }
`;

const WarningButton = styled(Button)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.error};
  border: 1px solid ${({ theme }) => theme.colors.error};
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.error}11;
    transform: translateY(-2px);
  }
`;

const DangerZone = styled.div`
  margin-top: ${({ theme }) => theme.spacing[8]};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  padding-top: ${({ theme }) => theme.spacing[6]};
`;

const DangerZoneTitle = styled.h3`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const DangerCard = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ theme }) => theme.colors.error}33;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const DangerCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[3]};
  }
`;

const DangerCardTitle = styled.h4`
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  margin: 0;
`;

const DangerCardDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ value }) => value}%;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    border-radius: ${({ theme }) => theme.borderRadius.full};
    transition: width 0.5s ease;
  }
`;

const StatusMessage = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    flex-shrink: 0;
  }
`;

const SuccessMessage = styled(StatusMessage)`
  background-color: ${({ theme }) => theme.colors.success}22;
  color: ${({ theme }) => theme.colors.success};
`;

const ErrorMessage = styled(StatusMessage)`
  background-color: ${({ theme }) => theme.colors.error}22;
  color: ${({ theme }) => theme.colors.error};
`;

// Payment related styled components
const PaymentCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.subtle};
  }
`;

const PaymentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const PaymentTitle = styled.h4`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: 18px;
  font-weight: 600;
`;

const PaymentAmount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #00CC66;
`;

const PaymentDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const PaymentDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const PaymentLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  font-weight: 500;
`;

const PaymentValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

const PaymentStatus = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  
  ${({ status }) => {
    switch (status) {
      case 'completed':
        return `
          background: #00CC6622;
          color: #00CC66;
        `;
      case 'pending':
        return `
          background: #f59e0b22;
          color: #f59e0b;
        `;
      case 'failed':
        return `
          background: #ef444422;
          color: #ef4444;
        `;
      default:
        return `
          background: #6b728022;
          color: #6b7280;
        `;
    }
  }}
`;

const PaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await api.payment.getPurchaseHistory();
      console.log('Payment history response:', response);
      // The API returns {orders: [...]} not {results: [...]}
      setPayments(response.orders || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      handleApiError(error, 'Failed to load payment history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatAmount = (amount) => {
    return `₹${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div>Loading payment history...</div>
      </div>
    );
  }

  return (
    <div>
      <SectionTitle><FiCreditCard /> Payment History</SectionTitle>

      {payments.length === 0 ? (
        <PaymentCard>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <FiCreditCard size={48} style={{ color: '#6b7280', marginBottom: '1rem' }} />
            <h4 style={{ color: '#6b7280', margin: '0 0 0.5rem 0' }}>No payments found</h4>
            <p style={{ color: '#9ca3af', margin: 0 }}>Your payment transactions will appear here</p>
          </div>
        </PaymentCard>
      ) : (
        payments.map((payment) => (
          <PaymentCard key={payment.id}>
            <PaymentHeader>
              <PaymentTitle>{payment.course?.title || 'Course Purchase'}</PaymentTitle>
              <PaymentAmount>{formatAmount(payment.amount_paid)}</PaymentAmount>
            </PaymentHeader>

            <PaymentDetails>
              <PaymentDetail>
                <PaymentLabel>Order Number</PaymentLabel>
                <PaymentValue>{payment.order_number}</PaymentValue>
              </PaymentDetail>

              <PaymentDetail>
                <PaymentLabel>Date</PaymentLabel>
                <PaymentValue>{formatDate(payment.purchased_at)}</PaymentValue>
              </PaymentDetail>

              <PaymentDetail>
                <PaymentLabel>Status</PaymentLabel>
                <PaymentStatus status={payment.status?.toLowerCase()}>
                  {payment.status}
                </PaymentStatus>
              </PaymentDetail>

              <PaymentDetail>
                <PaymentLabel>Payment Method</PaymentLabel>
                <PaymentValue>Razorpay</PaymentValue>
              </PaymentDetail>

              {payment.coupon_code && (
                <PaymentDetail>
                  <PaymentLabel>Coupon Used</PaymentLabel>
                  <PaymentValue>{payment.coupon_code}</PaymentValue>
                </PaymentDetail>
              )}

              {payment.discount_amount > 0 && (
                <PaymentDetail>
                  <PaymentLabel>Discount</PaymentLabel>
                  <PaymentValue>{formatAmount(payment.discount_amount)}</PaymentValue>
                </PaymentDetail>
              )}
            </PaymentDetails>
          </PaymentCard>
        ))
      )}
    </div>
  );
};

const Profile = () => {
  const { currentUser, updateProfile, logout, loading: authLoading } = useAuth();
    const [activeTab, setActiveTab] = useState('personal');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [profileData, setProfileData] = useState(null);

    // Form states
    const [personalInfo, setPersonalInfo] = useState({
        name: '',
        phone_number: '',
        college: '',
        register_number: '',
        branch: '',
        semester: '',
        bio: '',
        github_profile: '',
        linkedin_profile: '',
        areas_of_interest: []
    });

    const [passwordInfo, setPasswordInfo] = useState({
        old_password: '',
        new_password: '',
        confirm_password: ''
    });

    // UI states
    const [showPassword, setShowPassword] = useState({
        old: false,
        new: false,
        confirm: false
    });
    const [profileCompleteness, setProfileCompleteness] = useState(50);

  // Fetch fresh user data when component mounts (only once)
  useEffect(() => {
    let isMounted = true;

    const fetchUserProfile = async () => {
      // Don't fetch if we don't have a token
      const hasToken = localStorage.getItem('accessToken');
      if (!hasToken) {
        setError('Please log in to view your profile');
        return;
      }

      // Don't fetch if we already have currentUser data (avoid redundant calls)
      if (currentUser && Object.keys(currentUser).length > 3) {
        setProfileData(currentUser);
        return;
      }

      try {
        console.log('Profile: Fetching fresh user data (one-time fetch)...');
        setLoading(true);
        const freshUser = await api.users.getProfile();

        if (isMounted) {
          setProfileData(freshUser);
          console.log('Profile: Fresh user data received:', freshUser);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Profile: Error loading profile:', err);
          setError('Failed to load profile data. Please try logging in again.');

          // Use centralized error handling
          handleApiError(err, 'Failed to load profile data. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserProfile();

    return () => {
      isMounted = false;
    };
  }, []); // Only run once on mount

    // Update form state from user data
    useEffect(() => {
      const userData = profileData || currentUser;
      if (userData) {
            setPersonalInfo({
              name: userData.name || '',
              phone_number: userData.phone_number || '',
              college: userData.college || '',
              register_number: userData.register_number || '',
              branch: userData.branch || '',
              semester: userData.semester?.toString() || '',
              bio: userData.bio || '',
              github_profile: userData.github_profile || '',
              linkedin_profile: userData.linkedin_profile || '',
              areas_of_interest: userData.areas_of_interest || []
            });

            // Calculate profile completeness
            let fieldsToCheck = ['name', 'phone_number', 'college', 'register_number', 'branch', 'semester', 'bio', 'github_profile', 'linkedin_profile'];
        let filledFields = fieldsToCheck.filter(field => !!userData[field]).length;

            // Add areas_of_interest to calculation if it has items
        if (userData.areas_of_interest && userData.areas_of_interest.length > 0) {
                filledFields += 1;
                fieldsToCheck.push('areas_of_interest');
            }

            let completeness = Math.round((filledFields / fieldsToCheck.length) * 100);
            setProfileCompleteness(completeness);
        }
    }, [profileData, currentUser]);

    // Handle personal info change
    const handlePersonalInfoChange = (e) => {
        const { name, value } = e.target;
        setPersonalInfo(prev => ({
            ...prev,
            [name]: value
        }));

      // Clear validation error for this field
      if (validationErrors[name]) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    };

    // Handle password change
    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordInfo(prev => ({
            ...prev,
            [name]: value
        }));

      // Clear validation error for this field
      if (validationErrors[name]) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    };



  // Enhanced validation functions
  const validatePersonalInfo = () => {
    const errors = {};

    // Name validation
    if (!personalInfo.name?.trim()) {
      errors.name = 'Full name is required';
    } else if (personalInfo.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters long';
    } else if (!/^[a-zA-Z\s]+$/.test(personalInfo.name.trim())) {
      errors.name = 'Name should only contain letters and spaces';
    }

    // Phone number validation
    if (personalInfo.phone_number && !validatePhoneNumber(personalInfo.phone_number)) {
      errors.phone_number = 'Please enter a valid phone number (10 digits)';
    }

    // College validation
    if (personalInfo.college && personalInfo.college.trim().length < 2) {
      errors.college = 'College name must be at least 2 characters long';
    }

    // Register number validation
    if (personalInfo.register_number && personalInfo.register_number.trim().length < 3) {
      errors.register_number = 'Register number must be at least 3 characters long';
    }

    // Branch validation
    if (personalInfo.branch && personalInfo.branch.trim().length < 2) {
      errors.branch = 'Branch must be at least 2 characters long';
    }

    // Semester validation
    if (personalInfo.semester) {
      const sem = parseInt(personalInfo.semester);
      if (isNaN(sem) || sem < 1 || sem > 8) {
        errors.semester = 'Please select a valid semester (1-8)';
      }
    }

    // Bio validation
    if (personalInfo.bio && personalInfo.bio.length > 500) {
      errors.bio = 'Bio must be less than 500 characters';
    }

    // GitHub profile validation
    if (personalInfo.github_profile && personalInfo.github_profile.trim()) {
      const githubUrl = personalInfo.github_profile.trim();
      if (!githubUrl.includes('github.com') || !/^https?:\/\//i.test(githubUrl)) {
        errors.github_profile = 'Please enter a valid GitHub profile URL';
      }
    }

    // LinkedIn profile validation
    if (personalInfo.linkedin_profile && personalInfo.linkedin_profile.trim()) {
      const linkedinUrl = personalInfo.linkedin_profile.trim();
      if (!linkedinUrl.includes('linkedin.com') || !/^https?:\/\//i.test(linkedinUrl)) {
        errors.linkedin_profile = 'Please enter a valid LinkedIn profile URL';
      }
    }

    return errors;
  };

  const validatePasswordInfo = () => {
    const errors = {};

    // Current password validation
    if (!passwordInfo.old_password?.trim()) {
      errors.old_password = 'Current password is required';
    }

    // New password validation
    if (!passwordInfo.new_password?.trim()) {
      errors.new_password = 'New password is required';
    } else if (passwordInfo.new_password.length < 8) {
      errors.new_password = 'New password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordInfo.new_password)) {
      errors.new_password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    } else if (['password', '12345678', 'qwerty123'].includes(passwordInfo.new_password.toLowerCase())) {
      errors.new_password = 'Please choose a stronger password';
    }

    // Confirm password validation
    if (!passwordInfo.confirm_password?.trim()) {
      errors.confirm_password = 'Please confirm your new password';
    } else if (passwordInfo.new_password !== passwordInfo.confirm_password) {
      errors.confirm_password = 'New passwords do not match';
    }

    return errors;
  };

  // Handle phone number validation
  const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Optional field
    // Remove spaces and validate format - Indian phone numbers
    const cleanPhone = phone.replace(/\s+/g, '');
    return /^[6-9]\d{9}$/.test(cleanPhone) || /^\+91[6-9]\d{9}$/.test(cleanPhone);
    };

    // Handle personal info submit
    const handlePersonalInfoSubmit = async (e) => {
        e.preventDefault();

      // Clear previous messages
        setSuccess('');
        setError('');
      setValidationErrors({});

      // Frontend validation
      const errors = validatePersonalInfo();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setError('Please fix the validation errors below');
        return;
      }

      setLoading(true);

        try {
            const result = await updateProfile(personalInfo);

          if (result && result.success) {
                setSuccess('Profile information updated successfully');

                // Recalculate profile completeness
                let fieldsToCheck = ['name', 'phone_number', 'college', 'register_number', 'branch', 'semester', 'bio', 'github_profile', 'linkedin_profile'];
                let filledFields = fieldsToCheck.filter(field => !!personalInfo[field]).length;

                // Add areas_of_interest to calculation if it has items
                if (personalInfo.areas_of_interest && personalInfo.areas_of_interest.length > 0) {
                    filledFields += 1;
                    fieldsToCheck.push('areas_of_interest');
                }

                let completeness = Math.round((filledFields / fieldsToCheck.length) * 100);
                setProfileCompleteness(completeness);

            // Clear success message after 5 seconds
            setTimeout(() => setSuccess(''), 5000);
          } else if (result && result.error) {
            setError(result.error);
            // Handle field-specific validation errors from server
            if (result.validationErrors) {
              setValidationErrors(result.validationErrors);
            }
            } else {
            setError('Failed to update profile. Please try again.');
            }
        } catch (err) {
          console.error('Profile update error:', err);

          // Handle API errors - check for consistent error format
          if (err.response?.data?.error) {
            setError(err.response.data.error);
          } else if (err.response?.data?.detail) {
            setError(err.response.data.detail);
          } else if (err.message) {
            setError(err.message);
          } else {
            setError('Profile update failed. Please try again.');
          }
        } finally {
            setLoading(false);
        }
    };

    // Handle password change submit
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

      // Clear previous messages first
      setSuccess('');
      setError('');
      setValidationErrors({});

      // Frontend validation with comprehensive checks
      const errors = validatePasswordInfo();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setError('Please fix the validation errors below');
        return;
      }

      setLoading(true);

        try {
          const result = await api.users.changePassword({
            current_password: passwordInfo.old_password,
            new_password: passwordInfo.new_password
          });

          if (result && result.success) {
                setSuccess('Password changed successfully');
            showSuccess('Password changed successfully!');
                // Reset password fields
                setPasswordInfo({
                    old_password: '',
                    new_password: '',
                    confirm_password: ''
                });

            // Clear success message after 5 seconds
            setTimeout(() => setSuccess(''), 5000);
          } else if (result && result.error) {
            setError(result.error);
            handleApiError({ response: { data: { error: result.error } } }, result.error);
            } else {
            const errorMsg = 'Failed to change password. Please try again.';
            setError(errorMsg);
            handleApiError({}, errorMsg);
            }
        } catch (err) {
          console.error('Password change error:', err);

          // Use centralized error handling
          const errorMessage = handleApiError(err, 'Failed to change password. Please try again.');
          setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };


    // Handle logout
    const handleLogout = async () => {
        try {
            await logout();
            // Redirect is handled by AuthContext
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

  // Don't render if user data isn't loaded yet
  const userData = profileData || currentUser;
  if (!userData && (loading || authLoading)) {
    return (
      <Layout>
        <ProfileContainer>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            fontSize: '18px',
            color: '#666',
            gap: '10px'
          }}>
            <div>Loading profile...</div>
            {error && <div style={{ color: 'red', fontSize: '14px' }}>{error}</div>}
            {!localStorage.getItem('accessToken') && (
              <div style={{ fontSize: '14px', color: '#999' }}>
                Please <a href="/login" style={{ color: '#10ac84' }}>log in</a> to view your profile
              </div>
            )}
          </div>
        </ProfileContainer>
      </Layout>
    );
  }

  // If no user data and not loading, show error
  if (!userData) {
    return (
      <Layout>
        <ProfileContainer>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            fontSize: '18px',
            color: '#666',
            gap: '10px'
          }}>
            <div>Unable to load profile</div>
            {error && <div style={{ color: 'red', fontSize: '14px' }}>{error}</div>}
            <div style={{ fontSize: '14px', color: '#999' }}>
              Please <a href="/login" style={{ color: '#10ac84' }}>log in</a> to view your profile
            </div>
          </div>
        </ProfileContainer>
      </Layout>
    );
  }

    return (
        <Layout>
            <ProfileContainer>
                <ProfileHeader>
            {userData.profile_image ? (
              <ProfileImage src={userData.profile_image} alt={userData.name || 'Profile'} />
                    ) : (
                <Avatar>{(userData.name || userData.email || 'U').charAt(0).toUpperCase()}</Avatar>
                    )}

                    <ProfileInfo>
              <ProfileName>{userData.name || 'User'}</ProfileName>
              <ProfileEmail>{userData.email || ''}</ProfileEmail>

                        <StatsContainer>
                            <StatItem>
                  <StatLabel>XP</StatLabel>
                  <StatValue>{userData.reward_points || 0}</StatValue>
                            </StatItem>
                            <StatItem>
                                <StatLabel>Courses</StatLabel>
                  <StatValue>{userData.enrolled_courses_count || 0}</StatValue>
                            </StatItem>
                            <StatItem>
                                <StatLabel>Skill Level</StatLabel>
                  <StatValue>
                    {userData.skill_level
                      ? userData.skill_level.charAt(0).toUpperCase() + userData.skill_level.slice(1).toLowerCase()
                      : 'Beginner'
                    }
                  </StatValue>
                            </StatItem>
                        </StatsContainer>
                    </ProfileInfo>
                </ProfileHeader>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <TabsContainer>
                        <TabHeader>
                            <TabButton
                                isActive={activeTab === 'personal'}
                                onClick={() => setActiveTab('personal')}
                            >
                                <FiUser /> Personal Information
                            </TabButton>
                            <TabButton
                                isActive={activeTab === 'security'}
                                onClick={() => setActiveTab('security')}
                            >
                                <FiLock /> Security
                            </TabButton>
                <TabButton
                  isActive={activeTab === 'payments'}
                  onClick={() => setActiveTab('payments')}
                >
                  <FiCreditCard /> Payments
                </TabButton>
                            
                        </TabHeader>

                        <TabContent>
                            {activeTab === 'personal' && (
                                <div>
                                    <SectionTitle><FiUser /> Profile Information</SectionTitle>

                                    <ProgressBar value={profileCompleteness} />

                                    {success && <SuccessMessage><FiCheckCircle /> {success}</SuccessMessage>}
                                    {error && <ErrorMessage><FiXCircle /> {error}</ErrorMessage>}

                                    <Form onSubmit={handlePersonalInfoSubmit}>
                                        <FormRow>
                                            <FormGroup>
                                                <FormLabel><FiUser /> Full Name</FormLabel>
                                                <FormInput
                                                    type="text"
                                                    name="name"
                                                    value={personalInfo.name}
                                                    onChange={handlePersonalInfoChange}
                                                    placeholder="Enter your full name"
                            required
                            hasError={!!validationErrors.name}
                                                />
                          {validationErrors.name && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.name}
                            </ValidationError>
                          )}
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel><FiMail /> Email</FormLabel>
                                                <FormInput
                                                    type="email"
                            value={userData?.email || ''}
                                                    disabled
                                                />
                                            </FormGroup>
                                        </FormRow>

                                        <FormRow>
                                            <FormGroup>
                                                <FormLabel><FiPhone /> Phone Number</FormLabel>
                                                <FormInput
                                                    type="tel"
                                                    name="phone_number"
                                                    value={personalInfo.phone_number}
                                                    onChange={handlePersonalInfoChange}
                                                    placeholder="Enter your phone number"
                            hasError={!!validationErrors.phone_number}
                                                />
                          {validationErrors.phone_number && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.phone_number}
                            </ValidationError>
                          )}
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel><FiGlobe /> College/University</FormLabel>
                                                <FormInput
                                                    type="text"
                                                    name="college"
                                                    value={personalInfo.college}
                                                    onChange={handlePersonalInfoChange}
                                                    placeholder="Enter your college/university"
                            hasError={!!validationErrors.college}
                                                />
                          {validationErrors.college && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.college}
                            </ValidationError>
                          )}
                                            </FormGroup>
                                        </FormRow>

                                        <FormRow>
                                            <FormGroup>
                                                <FormLabel><FiBook /> Register Number</FormLabel>
                                                <FormInput
                                                    type="text"
                                                    name="register_number"
                                                    value={personalInfo.register_number}
                                                    onChange={handlePersonalInfoChange}
                                                    placeholder="Enter your register number"
                            hasError={!!validationErrors.register_number}
                                                />
                          {validationErrors.register_number && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.register_number}
                            </ValidationError>
                          )}
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel><FiBook /> Branch</FormLabel>
                                                <FormInput
                                                    type="text"
                                                    name="branch"
                                                    value={personalInfo.branch}
                                                    onChange={handlePersonalInfoChange}
                                                    placeholder="E.g. Computer Science"
                            hasError={!!validationErrors.branch}
                                                />
                          {validationErrors.branch && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.branch}
                            </ValidationError>
                          )}
                                            </FormGroup>
                                        </FormRow>

                                        <FormRow>
                                            <FormGroup>
                                                <FormLabel><FiBook /> Semester</FormLabel>
                                                <FormSelect
                                                    name="semester"
                                                    value={personalInfo.semester}
                                                    onChange={handlePersonalInfoChange}
                            hasError={!!validationErrors.semester}
                                                >
                                                    <option value="">Select your semester</option>
                                                    <option value="1">1st Semester</option>
                                                    <option value="2">2nd Semester</option>
                                                    <option value="3">3rd Semester</option>
                                                    <option value="4">4th Semester</option>
                                                    <option value="5">5th Semester</option>
                                                    <option value="6">6th Semester</option>
                                                    <option value="7">7th Semester</option>
                                                    <option value="8">8th Semester</option>
                                                </FormSelect>
                          {validationErrors.semester && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.semester}
                            </ValidationError>
                          )}
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel><FiBook /> Skill Level</FormLabel>
                                                <FormSelect
                                                    name="skill_level"
                                                    value={personalInfo.skill_level || "beginner"}
                                                    onChange={handlePersonalInfoChange}
                            hasError={!!validationErrors.skill_level}
                                                >
                                                    <option value="beginner">Beginner</option>
                                                    <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                                                </FormSelect>
                          {validationErrors.skill_level && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.skill_level}
                            </ValidationError>
                          )}
                                            </FormGroup>
                                        </FormRow>

                                        <FormGroup>
                                            <FormLabel><FiUser /> Bio</FormLabel>
                                            <FormTextarea
                                                name="bio"
                                                value={personalInfo.bio}
                                                onChange={handlePersonalInfoChange}
                                                placeholder="Tell us about yourself, your interests, and your cyber security journey..."
                          hasError={!!validationErrors.bio}
                                            />
                        {validationErrors.bio && (
                          <ValidationError>
                            <FiXCircle />
                            {validationErrors.bio}
                          </ValidationError>
                        )}
                                        </FormGroup>

                                        <FormRow>
                                            <FormGroup>
                                                <FormLabel><FiGithub /> GitHub Profile</FormLabel>
                                                <FormInput
                                                    type="text"
                                                    name="github_profile"
                                                    value={personalInfo.github_profile}
                                                    onChange={handlePersonalInfoChange}
                                                    placeholder="https://github.com/yourusername"
                            hasError={!!validationErrors.github_profile}
                                                />
                          {validationErrors.github_profile && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.github_profile}
                            </ValidationError>
                          )}
                                            </FormGroup>

                                            <FormGroup>
                                                <FormLabel><FiLinkedin /> LinkedIn Profile</FormLabel>
                                                <FormInput
                                                    type="text"
                                                    name="linkedin_profile"
                                                    value={personalInfo.linkedin_profile}
                                                    onChange={handlePersonalInfoChange}
                                                    placeholder="https://linkedin.com/in/yourusername"
                            hasError={!!validationErrors.linkedin_profile}
                                                />
                          {validationErrors.linkedin_profile && (
                            <ValidationError>
                              <FiXCircle />
                              {validationErrors.linkedin_profile}
                            </ValidationError>
                          )}
                                            </FormGroup>
                                        </FormRow>

                                        <ButtonGroup>
                                            <PrimaryButton type="submit" disabled={loading}>
                                                {loading ? 'Saving...' : 'Save Changes'}
                                            </PrimaryButton>
                                            <SecondaryButton type="button" onClick={() => setActiveTab('security')}>
                                                Manage Security
                                            </SecondaryButton>
                                        </ButtonGroup>
                                    </Form>
                                </div>
                            )}

                            {activeTab === 'security' && (
                                <div>
                                    <SectionTitle><FiShield /> Security Settings</SectionTitle>

                    {success && <SuccessMessage><FiCheckCircle /> {success}</SuccessMessage>}
                    {error && <ErrorMessage><FiXCircle /> {error}</ErrorMessage>}

                    <Form onSubmit={handlePasswordSubmit}>
                      <SecurityFormGroup>
                        <FormLabel htmlFor="old_password">Current Password</FormLabel>
                        <PasswordInputWrapper>
                          <PasswordInput
                            type={showPassword.old ? 'text' : 'password'}
                            name="old_password"
                            value={passwordInfo.old_password}
                            onChange={handlePasswordChange}
                            placeholder="Enter current password"
                            required
                            hasError={!!validationErrors.old_password}
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowPassword(prev => ({ ...prev, old: !prev.old }))}
                          >
                            {showPassword.old ? <FiEyeOff /> : <FiEye />}
                          </PasswordToggle>
                        </PasswordInputWrapper>
                        {validationErrors.old_password && (
                          <ValidationError>
                            <FiXCircle />
                            {validationErrors.old_password}
                          </ValidationError>
                        )}
                      </SecurityFormGroup>

                      <SecurityFormGroup>
                        <FormLabel htmlFor="new_password">New Password</FormLabel>
                        <PasswordInputWrapper>
                          <PasswordInput
                            type={showPassword.new ? 'text' : 'password'}
                            name="new_password"
                            value={passwordInfo.new_password}
                            onChange={handlePasswordChange}
                            placeholder="Enter new password (min. 8 characters)"
                            required
                            minLength="8"
                            hasError={!!validationErrors.new_password}
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                          >
                            {showPassword.new ? <FiEyeOff /> : <FiEye />}
                          </PasswordToggle>
                        </PasswordInputWrapper>
                        {validationErrors.new_password && (
                          <ValidationError>
                            <FiXCircle />
                            {validationErrors.new_password}
                          </ValidationError>
                        )}
                      </SecurityFormGroup>

                      <SecurityFormGroup>
                        <FormLabel htmlFor="confirm_password">Confirm New Password</FormLabel>
                        <PasswordInputWrapper>
                          <PasswordInput
                            type={showPassword.confirm ? 'text' : 'password'}
                            name="confirm_password"
                            value={passwordInfo.confirm_password}
                            onChange={handlePasswordChange}
                            placeholder="Confirm new password"
                            required
                            minLength="8"
                            hasError={!!validationErrors.confirm_password}
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                          >
                            {showPassword.confirm ? <FiEyeOff /> : <FiEye />}
                          </PasswordToggle>
                        </PasswordInputWrapper>
                        {validationErrors.confirm_password && (
                          <ValidationError>
                            <FiXCircle />
                            {validationErrors.confirm_password}
                          </ValidationError>
                        )}
                      </SecurityFormGroup>

                      <ButtonGroup>
                        <PrimaryButton type="submit" disabled={loading}>
                          {loading ? 'Changing Password...' : 'Change Password'}
                        </PrimaryButton>
                      </ButtonGroup>
                    </Form>

                                    <DangerZone>
                      <DangerZoneTitle><FiAlertTriangle /> Session Management</DangerZoneTitle>

                                        <DangerCard>
                                            <DangerCardHeader>
                          <DangerCardTitle>Logout from Account</DangerCardTitle>
                                                <WarningButton onClick={handleLogout}>
                                                    <FiLogOut /> Logout
                                                </WarningButton>
                                            </DangerCardHeader>
                                            <DangerCardDescription>
                                                This will end your current session. You will need to login again to access your account.
                                            </DangerCardDescription>
                                        </DangerCard>

                                    </DangerZone>
                                </div>
                            )}

                {activeTab === 'payments' && (
                  <PaymentsTab />
                )}

                        </TabContent>
                    </TabsContainer>
                </motion.div>
            </ProfileContainer>
        </Layout>
    );
};

export default Profile;